import React, { createContext, useContext, useState, useEffect } from 'react';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import { themeConfig } from './themeConfig';

// Create theme context
const ThemeContext = createContext();

// Custom hook to use theme context
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

// Theme provider component
const ThemeProvider = ({ children }) => {
  // Initialize theme from localStorage or default to light
  const [isDarkMode, setIsDarkMode] = useState(() => {
    try {
      const saved = localStorage.getItem('theme');
      return saved === 'dark' || (!saved && window.matchMedia('(prefers-color-scheme: dark)').matches);
    } catch (error) {
      return false;
    }
  });

  // Save theme preference to localStorage
  useEffect(() => {
    try {
      localStorage.setItem('theme', isDarkMode ? 'dark' : 'light');
    } catch (error) {
      console.warn('Could not save theme preference');
    }
  }, [isDarkMode]);

  // Apply CSS custom properties based on theme
  useEffect(() => {
    try {
      // Update document class for CSS variables - apply to both documentElement and body
      if (isDarkMode) {
        document.documentElement.classList.add('dark');
        document.documentElement.classList.remove('light');
        document.body.classList.add('dark');
        document.body.classList.remove('light');
      } else {
        document.documentElement.classList.add('light');
        document.documentElement.classList.remove('dark');
        document.body.classList.add('light');
        document.body.classList.remove('dark');
      }

      // Set CSS custom properties for consistent theming
      const theme = isDarkMode ? themeConfig.dark : themeConfig.light;
      const root = document.documentElement;

      // Background colors
      root.style.setProperty('--bg-primary', theme.palette.background.default);
      root.style.setProperty('--bg-secondary', theme.palette.background.paper);
      root.style.setProperty('--bg-sidebar', theme.palette.background.sidebar);
      root.style.setProperty('--bg-navbar', theme.palette.background.navbar);
      root.style.setProperty('--bg-card', theme.palette.background.card);

      // Text colors
      root.style.setProperty('--text-primary', theme.palette.text.primary);
      root.style.setProperty('--text-secondary', theme.palette.text.secondary);
      root.style.setProperty('--text-disabled', theme.palette.text.disabled);

      // Border colors
      root.style.setProperty('--border-color', theme.palette.border);
      root.style.setProperty('--divider-color', theme.palette.divider);

      // Primary colors
      root.style.setProperty('--primary-main', theme.palette.primary.main);
      root.style.setProperty('--primary-light', theme.palette.primary.light);
      root.style.setProperty('--primary-dark', theme.palette.primary.dark);

      // Status colors
      root.style.setProperty('--success-main', theme.palette.success.main);
      root.style.setProperty('--warning-main', theme.palette.warning.main);
      root.style.setProperty('--error-main', theme.palette.error.main);
      root.style.setProperty('--info-main', theme.palette.info.main);
    } catch (error) {
      console.warn('Could not apply theme styles:', error);
    }
  }, [isDarkMode]);

  // Toggle theme function
  const toggleTheme = () => {
    setIsDarkMode(prev => !prev);
  };

  // Get current theme object
  const currentTheme = isDarkMode ? themeConfig.dark : themeConfig.light;

  // Context value
  const value = {
    isDarkMode,
    toggleTheme,
    theme: currentTheme,
    colors: currentTheme.palette
  };

  return (
    <ThemeContext.Provider value={value}>
      <MuiThemeProvider theme={currentTheme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

export { ThemeProvider };
export default ThemeProvider;
